import { ReactNode, useEffect, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { useNavigate, useLocation } from "react-router-dom";
import { notifications } from "@mantine/notifications";

import CreateNewInlayCoreComponent from "./CreateNewInlayCoreComponent";
import CreateNewLay from "./CreateNewLay";
import CreateNewCrown from "./CreateNewCrown";
import CreateNewBridge from "./CreateNewBridge";
import CreateNewSplint from "./removable-items/CreateNewSplint";
import PartialDentureWorkflow from "./removable-items/partial-denture/PartialDentureworkflow";
import FullDentureWorkflow from "./removable-items/full-denture/fullDentureWorkflow";
import CreateNewImplantCrown from "./implants/CreateNewImplantCrown";
import CreateNewImplantBridge from "./implants/CreateNewImplantBridge";
import CreateNewScanOnly from "./CreateNewScanOnly";

import {
  ItemType,
  TeethShadeType,
  ItemShadeType,
  TeethShadeSide,
  ArchTypeEn,
  LayType,
} from "../../types/enums";
import { GET_ORDER_BY_UNIQUE_ATTRIBUTE } from "../../gql/orders";
import {
  ANATOMY_ITEM_CHOICE_DATA,
  CREATE_ANATOMY_ITEM,
  UPDATE_ANATOMY_ITEM,
  UPDATE_REMOVABLE_ITEM,
  CREATE_REMOVABLE_ITEM,
  DELETE_MANY_REMOVABLE_ITEMS,
  UPDATE_MANY_REMOVABLE_ITEMS,
  GET_IMPLANT_ITEMS_CHOICES,
  CREATE_IMPLANT_ITEM,
  UPDATE_IMPLANT_ITEM,
  DELETE_MANY_IMPLANT_ITEMS,
  DELETE_MANY_ANATOMY_ITEMS,
} from "../../gql/items";
import {
  UPDATE_PRODUCT,
  APPLY_PRODUCT_HAS_INDIVIDUAL_CHARACTERISTICS,
} from "../../gql/product";
import {
  GET_USER_AND_ACCOUNT_PRODUCT_PREFERENCES,
  GET_USER_PRODUCT_PREFERENCES,
} from "../../gql/users";
import {
  getAnatomyItemMaterialChoices,
  getAnatomyItemChoiceWhere,
  getAllPossibleSplintedCrown,
  getAllNonSplintedCrown,
  removeTeethFromBridgeOnInlayCore,
} from "../../utils/product.utils";
import {
  getTeethsFromUpperArch,
  getTeethsFromLowerArch,
  deduceFinalItemTypeOfFullDenture,
  isFullDentureType,
  getGingivaShadeFromAllShades,
  deduceItemTypeFromOptions,
} from "../../utils/item.utils";
import { FullDentureWorkflowToSubmit } from "./removable-items/full-denture/fullDentureInterfaces";
import { LowerArchTeeth, UpperArchTeeth } from "../../types/constants";
import MultiCharacteristicsWrapper from "./MultiCharacteristicsWrapper";
import { AnatomyItem } from "../../types/interfaces";

const NewItemComponent = () => {
  const [searchParams] = useSearchParams();
  const { product_id, order_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const productType = searchParams.get("itemType");
  const itemId = searchParams.get("itemId");

  const [openedPanelMultiCharacteristics, setOpenedPanelMultiCharacteristics] =
    useState<string>("");
  const [savedMultiCharacteristicsItems, setSavedMultiCharacteristicsItems] =
    useState<number[]>([]);

  const [createAnatomyItem] = useMutation(CREATE_ANATOMY_ITEM);
  const [updateAnatomyItem] = useMutation(UPDATE_ANATOMY_ITEM);
  const [deleteManyAnatomyItems] = useMutation(DELETE_MANY_ANATOMY_ITEMS);
  const [createRemovableItem] = useMutation(CREATE_REMOVABLE_ITEM);
  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM);
  const [updateManyRemovableItem] = useMutation(UPDATE_MANY_REMOVABLE_ITEMS);
  const [deleteManyRemovableItem] = useMutation(DELETE_MANY_REMOVABLE_ITEMS);
  const [updateImplantItem] = useMutation(UPDATE_IMPLANT_ITEM);
  const [createImplantItem] = useMutation(CREATE_IMPLANT_ITEM);
  const [deleteManyImplantItems] = useMutation(DELETE_MANY_IMPLANT_ITEMS);
  const [updateProduct] = useMutation(UPDATE_PRODUCT);
  const [applyProductHasIndividualCharacteristics] = useMutation(
    APPLY_PRODUCT_HAS_INDIVIDUAL_CHARACTERISTICS
  );

  const {
    data: order,
    refetch,
    loading,
  } = useQuery(GET_ORDER_BY_UNIQUE_ATTRIBUTE, {
    variables: {
      where: {
        id: order_id,
      },
    },
  });

  const { data: implantItemChoicesData } = useQuery(GET_IMPLANT_ITEMS_CHOICES, {
    fetchPolicy: "network-only",
  });

  const { data: anatomyItemChoicesData } = useQuery(ANATOMY_ITEM_CHOICE_DATA, {
    variables: {
      where: getAnatomyItemChoiceWhere(productType),
    },
    fetchPolicy: "network-only",
  });

  const { data: accountProductPreferences } = useQuery(
    GET_USER_AND_ACCOUNT_PRODUCT_PREFERENCES
  );

  const [queryUserProductPreferences] = useLazyQuery(
    GET_USER_PRODUCT_PREFERENCES
  );

  const materialFormChoices = anatomyItemChoicesData
    ? getAnatomyItemMaterialChoices(anatomyItemChoicesData, productType)
    : [];

  const deleteAnatomyItemById = async (itemId: string) => {
    await deleteManyAnatomyItems({
      variables: {
        where: {
          id: {
            equals: itemId,
          },
        },
      },
    });
  };

  const deleteAnatomyItemByProductIdAndTeeth = async (
    product_id: string,
    teeth: number[]
  ) => {
    await deleteManyAnatomyItems({
      variables: {
        where: {
          productId: {
            equals: product_id,
          },
          teeth: {
            equals: teeth,
          },
        },
      },
    });
  };
  const deleteAnatomyItemByProductId = async (product_id: string) => {
    await deleteManyAnatomyItems({
      variables: {
        where: {
          productId: {
            equals: product_id,
          },
        },
      },
    });
  };

  const updateProductHasMultiCharacteristics = async (
    product_id: string,
    productHasMultiCharacteristics: boolean
  ) => {
    await updateProduct({
      variables: {
        where: {
          id: product_id,
        },
        data: {
          hasIndividualItemCaracteristics: {
            set: productHasMultiCharacteristics,
          },
        },
      },
    });
  };

  const product = (order?.getOrderByUniqueAttribute?.products ?? []).find(
    product => product.id === product_id
  );

  const [isMultipleCaracteristicsByItem, setIsMultipleCaracteristicsByItem] =
    useState<boolean>(product?.hasIndividualItemCaracteristics);

  const orderAnatomyItems = product?.anatomyItem ?? [];

  useEffect(() => {
    setIsMultipleCaracteristicsByItem(product?.hasIndividualItemCaracteristics);
  }, [product?.hasIndividualItemCaracteristics]);

  if (loading) {
    return <>Loading...</>;
  }

  switch (productType) {
    case ItemType.INLAY_CORE:
      return (
        <MultiCharacteristicsWrapper
          items={orderAnatomyItems}
          isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
          openedPanelMultiCharacteristics={openedPanelMultiCharacteristics}
          setOpenedPanelMultiCharacteristics={title =>
            setOpenedPanelMultiCharacteristics(title)
          }
          savedMultiCharacteristicsItems={savedMultiCharacteristicsItems}
          setIsMultipleCaracteristicsByItem={async value => {
            setIsMultipleCaracteristicsByItem(value);
            if (value) {
              await applyProductHasIndividualCharacteristics({
                variables: {
                  productId: product_id,
                  itemsHaveIndividualCaracteristics: true,
                },
              });
              await refetch();
            }
          }}
        >
          {orderAnatomyItems => (
            <CreateNewInlayCoreComponent
              productType={productType}
              order={order?.getOrderByUniqueAttribute}
              product_id={product_id}
              refetch={refetch}
              itemId={itemId}
              materialFormChoices={materialFormChoices}
              anatomyItemChoicesData={anatomyItemChoicesData ?? []}
              accountProductPreferences={
                accountProductPreferences?.getAccountProductPreferences
              }
              item={orderAnatomyItems}
              isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
              onSubmit={async (
                data,
                watchIsInlayCoreOnly: boolean,
                watchIsMultiShade,
                watchInlayCoreScanPostType,
                isNotBridgeOnInlayCore,
                savedItem
              ) => {
                if (isMultipleCaracteristicsByItem) {
                  setSavedMultiCharacteristicsItems([
                    ...savedItem.teeth,
                    ...savedMultiCharacteristicsItems,
                  ]);
                }
                setOpenedPanelMultiCharacteristics("");
                await updateProductHasMultiCharacteristics(
                  product_id,
                  isMultipleCaracteristicsByItem
                );
                const scanPostSelected =
                  watchInlayCoreScanPostType === "ANATOMICAL"
                    ? watchInlayCoreScanPostType
                    : (data?.inlayCoreScanPost ?? watchInlayCoreScanPostType);
                if (savedItem?.teeth) {
                  if (isMultipleCaracteristicsByItem) {
                    await deleteAnatomyItemByProductIdAndTeeth(
                      product_id,
                      savedItem?.teeth
                    ); // should delete crown as well, maybe deleteing all objects
                  } else {
                    await deleteAnatomyItemByProductId(product_id);
                  }
                }
                const itemTeeth = isMultipleCaracteristicsByItem
                  ? savedItem?.teeth
                  : product?.teeth;
                const allNonPossibleSplintedCrown: number[] =
                  removeTeethFromBridgeOnInlayCore(
                    getAllNonSplintedCrown(itemTeeth),
                    order?.products ?? []
                  );
                const allSplinteCrown: number[][] =
                  getAllPossibleSplintedCrown(itemTeeth);
                const productToUpdate = [];
                if (
                  data.isSplintedCrown &&
                  !data.isInlayCoreOnly &&
                  isNotBridgeOnInlayCore
                ) {
                  allSplinteCrown.map(splintedCrown => {
                    const splintedCrownTeeth = removeTeethFromBridgeOnInlayCore(
                      splintedCrown,
                      order?.products ?? []
                    );
                    if (splintedCrownTeeth.length > 0) {
                      productToUpdate.push({
                        inlayCoreHasClavette: data.isInlayCoreClaveted,
                        inlayCoreScanPost: scanPostSelected,
                        bridgeType: data.bridgeType ?? undefined,
                        inlayCoreMaterial: data.inlayCoreMaterial
                          ? {
                              connect: {
                                id: data.inlayCoreMaterial,
                              },
                            }
                          : undefined,
                        itemMaterial:
                          data.material && !watchIsInlayCoreOnly
                            ? {
                                connect: {
                                  id: data.material,
                                },
                              }
                            : undefined,
                        itemShade:
                          data.shade && !watchIsMultiShade
                            ? {
                                connect: {
                                  id: data.shade,
                                },
                              }
                            : undefined,
                        itemType: ItemType.SPLINTED_CROWN,
                        teeth: splintedCrownTeeth,
                        product: {
                          connect: {
                            id: product_id,
                          },
                        },
                        teethshadeType: watchIsMultiShade
                          ? TeethShadeType.MULTI_SHADE
                          : TeethShadeType.SINGLE_SHADE,
                        multiShadeInfo: watchIsMultiShade
                          ? {
                              createMany: {
                                data: [
                                  ...(data.gingivalShade
                                    ? [
                                        {
                                          itemShadeId: data.gingivalShade,
                                          teethShadeSide:
                                            TeethShadeSide.GINGIVAL,
                                        },
                                      ]
                                    : []),
                                  ...(data.baseShade
                                    ? [
                                        {
                                          itemShadeId: data.baseShade,
                                          teethShadeSide: TeethShadeSide.BASE,
                                        },
                                      ]
                                    : []),
                                  ...(data.incisalShade
                                    ? [
                                        {
                                          itemShadeId: data.incisalShade,
                                          teethShadeSide:
                                            TeethShadeSide.INCISAL,
                                        },
                                      ]
                                    : []),
                                ],
                              },
                            }
                          : undefined,
                      });
                    }
                  });
                  if (
                    allNonPossibleSplintedCrown.length > 0 &&
                    isNotBridgeOnInlayCore
                  ) {
                    productToUpdate.push({
                      inlayCoreHasClavette: data.isInlayCoreClaveted,
                      inlayCoreMaterial: data.inlayCoreMaterial
                        ? {
                            connect: {
                              id: data.inlayCoreMaterial,
                            },
                          }
                        : undefined,
                      itemShade:
                        data.shade && !watchIsMultiShade
                          ? {
                              connect: {
                                id: data.shade,
                              },
                            }
                          : undefined,
                      inlayCoreScanPost: scanPostSelected ?? undefined,
                      itemType: ItemType.CROWN ?? undefined,
                      bridgeType: data.bridgeType ?? undefined,
                      teeth: allNonPossibleSplintedCrown,
                      itemMaterial:
                        data.material && !watchIsInlayCoreOnly
                          ? {
                              connect: {
                                id: data.material,
                              },
                            }
                          : undefined,
                      product: {
                        connect: {
                          id: product.id,
                        },
                      },
                      teethshadeType: watchIsMultiShade
                        ? TeethShadeType.MULTI_SHADE
                        : TeethShadeType.SINGLE_SHADE,
                      multiShadeInfo: watchIsMultiShade
                        ? {
                            createMany: {
                              data: [
                                ...(data.gingivalShade
                                  ? [
                                      {
                                        itemShadeId: data.gingivalShade,
                                        teethShadeSide: TeethShadeSide.GINGIVAL,
                                      },
                                    ]
                                  : []),
                                ...(data.baseShade
                                  ? [
                                      {
                                        itemShadeId: data.baseShade,
                                        teethShadeSide: TeethShadeSide.BASE,
                                      },
                                    ]
                                  : []),
                                ...(data.incisalShade
                                  ? [
                                      {
                                        itemShadeId: data.incisalShade,
                                        teethShadeSide: TeethShadeSide.INCISAL,
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          }
                        : undefined,
                    });
                  }
                }
                const itemTypeToUpdate = deduceItemTypeFromOptions(
                  data,
                  product
                );
                if (
                  product.productType === ItemType.CROWN
                    ? !data.isSplintedCrown
                    : true
                ) {
                  productToUpdate.push({
                    inlayCoreHasClavette: data.isInlayCoreClaveted,
                    inlayCoreMaterial: data.inlayCoreMaterial
                      ? {
                          connect: {
                            id: data.inlayCoreMaterial,
                          },
                        }
                      : undefined,
                    itemShade:
                      data.shade &&
                      !watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            connect: {
                              id: data.shade,
                            },
                          }
                        : undefined,
                    inlayCoreScanPost: scanPostSelected ?? undefined,
                    itemType: itemTypeToUpdate ?? undefined,
                    bridgeType: data.bridgeType ?? undefined,
                    teeth: itemTeeth,
                    itemMaterial:
                      data.material && !watchIsInlayCoreOnly
                        ? {
                            connect: {
                              id: data.material,
                            },
                          }
                        : undefined,
                    product: {
                      connect: {
                        id: product.id,
                      },
                    },
                    teethshadeType:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? TeethShadeType.MULTI_SHADE
                        : TeethShadeType.SINGLE_SHADE,
                    multiShadeInfo:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            createMany: {
                              data: [
                                ...(data.gingivalShade
                                  ? [
                                      {
                                        itemShadeId: data.gingivalShade,
                                        teethShadeSide: TeethShadeSide.GINGIVAL,
                                      },
                                    ]
                                  : []),
                                ...(data.baseShade
                                  ? [
                                      {
                                        itemShadeId: data.baseShade,
                                        teethShadeSide: TeethShadeSide.BASE,
                                      },
                                    ]
                                  : []),
                                ...(data.incisalShade
                                  ? [
                                      {
                                        itemShadeId: data.incisalShade,
                                        teethShadeSide: TeethShadeSide.INCISAL,
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          }
                        : undefined,
                  });
                }
                const crownTeeths = removeTeethFromBridgeOnInlayCore(
                  itemTeeth,
                  order?.products ?? []
                );
                if (
                  product.productType === ItemType.INLAY_CORE &&
                  !data.isInlayCoreOnly &&
                  !data.isRichmondCrown &&
                  !data.isSplintedCrown &&
                  isNotBridgeOnInlayCore &&
                  crownTeeths.length > 0
                ) {
                  productToUpdate.push({
                    inlayCoreHasClavette: data.isInlayCoreClaveted,
                    inlayCoreScanPost: scanPostSelected ?? undefined,
                    bridgeType: data.bridgeType ?? undefined,
                    inlayCoreMaterial: data.inlayCoreMaterial
                      ? {
                          connect: {
                            id: data.inlayCoreMaterial,
                          },
                        }
                      : undefined,
                    itemMaterial:
                      data.material && !watchIsInlayCoreOnly
                        ? {
                            connect: {
                              id: data.material,
                            },
                          }
                        : undefined,
                    itemShade:
                      data.shade &&
                      !watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            connect: {
                              id: data.shade,
                            },
                          }
                        : undefined,
                    itemType: ItemType.CROWN,
                    teeth: crownTeeths,
                    product: {
                      connect: {
                        id: product.id,
                      },
                    },
                    teethshadeType:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? TeethShadeType.MULTI_SHADE
                        : TeethShadeType.SINGLE_SHADE,
                    multiShadeInfo:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            createMany: {
                              data: [
                                ...(data.gingivalShade
                                  ? [
                                      {
                                        itemShadeId: data.gingivalShade,
                                        teethShadeSide: TeethShadeSide.GINGIVAL,
                                      },
                                    ]
                                  : []),
                                ...(data.baseShade
                                  ? [
                                      {
                                        itemShadeId: data.baseShade,
                                        teethShadeSide: TeethShadeSide.BASE,
                                      },
                                    ]
                                  : []),
                                ...(data.incisalShade
                                  ? [
                                      {
                                        itemShadeId: data.incisalShade,
                                        teethShadeSide: TeethShadeSide.INCISAL,
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          }
                        : undefined,
                  });
                }
                await Promise.all(
                  productToUpdate.map(item =>
                    createAnatomyItem({
                      variables: {
                        args: item,
                      },
                    })
                  )
                );
                await refetch();
                if (isMultipleCaracteristicsByItem) {
                  notifications.show({
                    title: "Update saved",
                    color: "green",
                    message: "",
                  });
                  return;
                }
                navigate(
                  `/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`
                );
              }}
            />
          )}
        </MultiCharacteristicsWrapper>
      );
    case ItemType.LAY:
    case ItemType.INLAY:
    case ItemType.ONLAY:
    case ItemType.OVERLAY:
      return (
        <MultiCharacteristicsWrapper
          items={orderAnatomyItems}
          isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
          openedPanelMultiCharacteristics={openedPanelMultiCharacteristics}
          setOpenedPanelMultiCharacteristics={title =>
            setOpenedPanelMultiCharacteristics(title)
          }
          savedMultiCharacteristicsItems={savedMultiCharacteristicsItems}
          setIsMultipleCaracteristicsByItem={async value => {
            setIsMultipleCaracteristicsByItem(value);
            if (value) {
              await applyProductHasIndividualCharacteristics({
                variables: {
                  productId: product_id,
                  itemsHaveIndividualCaracteristics: true,
                },
              });
              await refetch();
            }
          }}
        >
          {orderAnatomyItems => (
            <CreateNewLay
              productType={productType}
              materialFormChoices={materialFormChoices}
              anatomyItemChoicesData={anatomyItemChoicesData ?? []}
              accountProductPreferences={
                accountProductPreferences?.getAccountProductPreferences
              }
              order={order?.getOrderByUniqueAttribute}
              product_id={product_id}
              refetch={refetch}
              item={orderAnatomyItems}
              isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
              onSubmit={async (
                data: {
                  material: string;
                  shade?: string;
                  gingivalShade?: string;
                  baseShade?: string;
                  incisalShade?: string;
                },
                watchIsMultiShade: boolean,
                watchLayType: LayType,
                savedItem: AnatomyItem
              ) => {
                if (isMultipleCaracteristicsByItem) {
                  setSavedMultiCharacteristicsItems([
                    ...savedItem.teeth,
                    ...savedMultiCharacteristicsItems,
                  ]);
                }
                setOpenedPanelMultiCharacteristics("");
                await updateProductHasMultiCharacteristics(
                  product_id,
                  isMultipleCaracteristicsByItem
                );
                if (savedItem?.teeth) {
                  if (isMultipleCaracteristicsByItem) {
                    await deleteAnatomyItemById(savedItem?.id);
                  } else {
                    await deleteAnatomyItemByProductId(product_id);
                  }
                }
                const itemTeeth = isMultipleCaracteristicsByItem
                  ? savedItem?.teeth
                  : product?.teeth;
                await createAnatomyItem({
                  variables: {
                    args: {
                      itemMaterial: data.material
                        ? {
                            connect: {
                              id: data.material,
                            },
                          }
                        : undefined,
                      itemShade:
                        data.shade &&
                        !watchIsMultiShade &&
                        data.material !== "METAL_NON_PRECIOUS"
                          ? {
                              connect: {
                                id: data.shade,
                              },
                            }
                          : undefined,
                      itemType:
                        productType === ItemType.LAY
                          ? watchLayType
                          : productType,
                      teeth: itemTeeth,
                      product: {
                        connect: {
                          id: product.id,
                        },
                      },
                      teethshadeType:
                        watchIsMultiShade &&
                        data.material !== "METAL_NON_PRECIOUS"
                          ? TeethShadeType.MULTI_SHADE
                          : TeethShadeType.SINGLE_SHADE,
                      multiShadeInfo:
                        watchIsMultiShade &&
                        data.material !== "METAL_NON_PRECIOUS"
                          ? {
                              createMany: {
                                data: [
                                  ...(data.gingivalShade
                                    ? [
                                        {
                                          itemShadeId: data.gingivalShade,
                                          teethShadeSide:
                                            TeethShadeSide.GINGIVAL,
                                        },
                                      ]
                                    : []),
                                  ...(data.baseShade
                                    ? [
                                        {
                                          itemShadeId: data.baseShade,
                                          teethShadeSide: TeethShadeSide.BASE,
                                        },
                                      ]
                                    : []),
                                  ...(data.incisalShade
                                    ? [
                                        {
                                          itemShadeId: data.incisalShade,
                                          teethShadeSide:
                                            TeethShadeSide.INCISAL,
                                        },
                                      ]
                                    : []),
                                ],
                              },
                            }
                          : undefined,
                    },
                  },
                });
                await refetch();
                if (isMultipleCaracteristicsByItem) {
                  notifications.show({
                    title: "Update saved",
                    color: "green",
                    message: "",
                  });
                  return;
                }
                navigate(
                  `/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`
                );
              }}
            />
          )}
        </MultiCharacteristicsWrapper>
      );
    case ItemType.SPLINT:
      return (
        <CreateNewSplint
          order={order?.getOrderByUniqueAttribute}
          product_id={product_id}
          refetch={refetch}
        />
      );
    case ItemType.BRIDGE:
      return (
        <CreateNewBridge
          materialFormChoices={materialFormChoices}
          anatomyItemChoicesData={anatomyItemChoicesData ?? []}
          accountProductPreferences={
            accountProductPreferences?.getAccountProductPreferences
          }
          order={order?.getOrderByUniqueAttribute}
          productId={product_id}
          onSubmit={async () => {
            await refetch();
            navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`);
          }}
        />
      );
    case ItemType.VENEER:
      return (
        <CreateNewCrown
          materialFormChoices={materialFormChoices}
          anatomyItemChoicesData={anatomyItemChoicesData ?? []}
          accountProductPreferences={
            accountProductPreferences?.getAccountProductPreferences
          }
          order={order?.getOrderByUniqueAttribute}
          product_id={product_id}
          itemType={ItemType.VENEER}
          onSubmit={async (data, watchIsMultiShade: boolean, item) => {
            if (item?.id) {
              if ((item?.multiShadeInfo ?? []).length > 0) {
                await updateAnatomyItem({
                  variables: {
                    where: {
                      id: item.id,
                    },
                    data: {
                      multiShadeInfo: {
                        deleteMany: [
                          {
                            id: {
                              in: (item?.multiShadeInfo).map(
                                (multiShadeInfo: { id: string }) =>
                                  multiShadeInfo.id
                              ),
                            },
                          },
                        ],
                      },
                    },
                  },
                });
              }
              await updateAnatomyItem({
                variables: {
                  where: {
                    id: item?.id,
                  },
                  data: {
                    itemMaterial: data.material
                      ? {
                          connect: {
                            id: data.material,
                          },
                        }
                      : undefined,
                    itemShade:
                      data.shade &&
                      !watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            connect: {
                              id: data.shade,
                            },
                          }
                        : undefined,
                    teethshadeType: {
                      set:
                        watchIsMultiShade &&
                        data.material !== "METAL_NON_PRECIOUS"
                          ? TeethShadeType.MULTI_SHADE
                          : TeethShadeType.SINGLE_SHADE,
                    },
                    multiShadeInfo:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            createMany: {
                              data: [
                                ...(data.gingivalShade
                                  ? [
                                      {
                                        itemShadeId: data.gingivalShade,
                                        teethShadeSide: TeethShadeSide.GINGIVAL,
                                      },
                                    ]
                                  : []),
                                ...(data.baseShade
                                  ? [
                                      {
                                        itemShadeId: data.baseShade,
                                        teethShadeSide: TeethShadeSide.BASE,
                                      },
                                    ]
                                  : []),
                                ...(data.incisalShade
                                  ? [
                                      {
                                        itemShadeId: data.incisalShade,
                                        teethShadeSide: TeethShadeSide.INCISAL,
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          }
                        : undefined,
                  },
                },
              });
            } else {
              await createAnatomyItem({
                variables: {
                  args: {
                    itemMaterial: data.material
                      ? {
                          connect: {
                            id: data.material,
                          },
                        }
                      : undefined,
                    itemShade:
                      data.shade &&
                      !watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            connect: {
                              id: data.shade,
                            },
                          }
                        : undefined,
                    itemType: ItemType.VENEER,
                    teeth: product?.teeth,
                    product: {
                      connect: {
                        id: product.id,
                      },
                    },
                    teethshadeType:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? TeethShadeType.MULTI_SHADE
                        : TeethShadeType.SINGLE_SHADE,
                    multiShadeInfo:
                      watchIsMultiShade &&
                      data.material !== "METAL_NON_PRECIOUS"
                        ? {
                            createMany: {
                              data: [
                                ...(data.gingivalShade
                                  ? [
                                      {
                                        itemShadeId: data.gingivalShade,
                                        teethShadeSide: TeethShadeSide.GINGIVAL,
                                      },
                                    ]
                                  : []),
                                ...(data.baseShade
                                  ? [
                                      {
                                        itemShadeId: data.baseShade,
                                        teethShadeSide: TeethShadeSide.BASE,
                                      },
                                    ]
                                  : []),
                                ...(data.incisalShade
                                  ? [
                                      {
                                        itemShadeId: data.incisalShade,
                                        teethShadeSide: TeethShadeSide.INCISAL,
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          }
                        : undefined,
                  },
                },
              });
            }
            await refetch();
            navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`);
          }}
        />
      );
    case ItemType.CROWN:
      return (
        <>
          <MultiCharacteristicsWrapper
            items={orderAnatomyItems}
            isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
            openedPanelMultiCharacteristics={openedPanelMultiCharacteristics}
            setOpenedPanelMultiCharacteristics={title =>
              setOpenedPanelMultiCharacteristics(title)
            }
            savedMultiCharacteristicsItems={savedMultiCharacteristicsItems}
            setIsMultipleCaracteristicsByItem={async value => {
              setIsMultipleCaracteristicsByItem(value);
              if (value) {
                await applyProductHasIndividualCharacteristics({
                  variables: {
                    productId: product_id,
                    itemsHaveIndividualCaracteristics: true,
                  },
                });
                await refetch();
              }
            }}
          >
            {orderAnatomyItems => (
              <CreateNewCrown
                materialFormChoices={materialFormChoices}
                anatomyItemChoicesData={anatomyItemChoicesData ?? []}
                accountProductPreferences={
                  accountProductPreferences?.getAccountProductPreferences
                }
                order={order?.getOrderByUniqueAttribute}
                product_id={product_id}
                itemType={ItemType.CROWN}
                item={orderAnatomyItems}
                isMultipleCaracteristicsByItem={isMultipleCaracteristicsByItem}
                onSubmit={async (data, watchIsMultiShade, item) => {
                  if (isMultipleCaracteristicsByItem) {
                    setSavedMultiCharacteristicsItems([
                      ...item.teeth,
                      ...savedMultiCharacteristicsItems,
                    ]);
                  }
                  setOpenedPanelMultiCharacteristics("");
                  await updateProductHasMultiCharacteristics(
                    product_id,
                    isMultipleCaracteristicsByItem
                  );
                  if (isMultipleCaracteristicsByItem) {
                    await deleteAnatomyItemById(item?.id);
                  } else {
                    await deleteAnatomyItemByProductId(product_id);
                  }
                  const productTeeth = isMultipleCaracteristicsByItem
                    ? item?.teeth
                    : product?.teeth;
                  const allNonSplintedCrown = getAllNonSplintedCrown(
                    productTeeth ?? []
                  );
                  if (data.isSplintedCrown) {
                    const allSplinteCrown: number[][] =
                      getAllPossibleSplintedCrown(productTeeth ?? []);
                    Promise.all(
                      allSplinteCrown.map(
                        async (splintedCrownTeeth: number[]) => {
                          await createAnatomyItem({
                            variables: {
                              args: {
                                itemMaterial: data.material
                                  ? {
                                      connect: {
                                        id: data.material,
                                      },
                                    }
                                  : undefined,
                                itemShade:
                                  data.shade &&
                                  !watchIsMultiShade &&
                                  data.material !== "METAL_NON_PRECIOUS"
                                    ? {
                                        connect: {
                                          id: data.shade,
                                        },
                                      }
                                    : undefined,
                                itemType: ItemType.SPLINTED_CROWN,
                                teeth: splintedCrownTeeth,
                                product: {
                                  connect: {
                                    id: product.id,
                                  },
                                },
                                teethshadeType:
                                  watchIsMultiShade &&
                                  data.material !== "METAL_NON_PRECIOUS"
                                    ? TeethShadeType.MULTI_SHADE
                                    : TeethShadeType.SINGLE_SHADE,
                                multiShadeInfo:
                                  watchIsMultiShade &&
                                  data.material !== "METAL_NON_PRECIOUS"
                                    ? {
                                        createMany: {
                                          data: [
                                            ...(data.gingivalShade
                                              ? [
                                                  {
                                                    itemShadeId:
                                                      data.gingivalShade,
                                                    teethShadeSide:
                                                      TeethShadeSide.GINGIVAL,
                                                  },
                                                ]
                                              : []),
                                            ...(data.baseShade
                                              ? [
                                                  {
                                                    itemShadeId: data.baseShade,
                                                    teethShadeSide:
                                                      TeethShadeSide.BASE,
                                                  },
                                                ]
                                              : []),
                                            ...(data.incisalShade
                                              ? [
                                                  {
                                                    itemShadeId:
                                                      data.incisalShade,
                                                    teethShadeSide:
                                                      TeethShadeSide.INCISAL,
                                                  },
                                                ]
                                              : []),
                                          ],
                                        },
                                      }
                                    : undefined,
                              },
                            },
                          });
                        }
                      )
                    );
                  }
                  if (!data.isSplintedCrown || allNonSplintedCrown.length > 0) {
                    await createAnatomyItem({
                      variables: {
                        args: {
                          itemMaterial: data.material
                            ? {
                                connect: {
                                  id: data.material,
                                },
                              }
                            : undefined,
                          itemShade:
                            data.shade &&
                            !watchIsMultiShade &&
                            data.material !== "METAL_NON_PRECIOUS"
                              ? {
                                  connect: {
                                    id: data.shade,
                                  },
                                }
                              : undefined,
                          itemType: ItemType.CROWN,
                          teeth: data.isSplintedCrown
                            ? allNonSplintedCrown
                            : productTeeth,
                          product: {
                            connect: {
                              id: product.id,
                            },
                          },
                          teethshadeType:
                            watchIsMultiShade &&
                            data.material !== "METAL_NON_PRECIOUS"
                              ? TeethShadeType.MULTI_SHADE
                              : TeethShadeType.SINGLE_SHADE,
                          multiShadeInfo:
                            watchIsMultiShade &&
                            data.material !== "METAL_NON_PRECIOUS"
                              ? {
                                  createMany: {
                                    data: [
                                      ...(data.gingivalShade
                                        ? [
                                            {
                                              itemShadeId: data.gingivalShade,
                                              teethShadeSide:
                                                TeethShadeSide.GINGIVAL,
                                            },
                                          ]
                                        : []),
                                      ...(data.baseShade
                                        ? [
                                            {
                                              itemShadeId: data.baseShade,
                                              teethShadeSide:
                                                TeethShadeSide.BASE,
                                            },
                                          ]
                                        : []),
                                      ...(data.incisalShade
                                        ? [
                                            {
                                              itemShadeId: data.incisalShade,
                                              teethShadeSide:
                                                TeethShadeSide.INCISAL,
                                            },
                                          ]
                                        : []),
                                    ],
                                  },
                                }
                              : undefined,
                        },
                      },
                    });
                  }
                  await refetch();
                  if (isMultipleCaracteristicsByItem) {
                    notifications.show({
                      title: "Update saved",
                      color: "green",
                      message: "",
                    });
                    return;
                  }
                  navigate(
                    `/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`
                  );
                }}
              />
            )}
          </MultiCharacteristicsWrapper>
        </>
      );
    case ItemType.FULL_DENTURE:
    case ItemType.IMMEDIATE_DENTURE:
    case ItemType.FULL_DENTURE_WAX_BITE:
      return (
        <FullDentureWorkflow
          user_id={order?.getOrderByUniqueAttribute?.user?.id}
          accountProductPreferences={
            accountProductPreferences?.getAccountProductPreferences
          }
          gingivaShadeChoices={getGingivaShadeFromAllShades(
            anatomyItemChoicesData?.getItemShadesWhere ?? []
          )}
          anatomyItemChoicesData={anatomyItemChoicesData ?? []}
          order={order?.getOrderByUniqueAttribute}
          onSubmit={async (data: FullDentureWorkflowToSubmit) => {
            const savedItem = (
              location?.state?.product?.removableItem ?? []
            ).find((item: { itemType: ItemType }) =>
              isFullDentureType(item.itemType)
            );
            await updateProduct({
              variables: {
                where: {
                  id: product.id,
                },
                data: {
                  itemPreferences: {
                    set: {
                      teethShape: data.teethShape,
                    },
                  },
                },
              },
            });
            const finalItemType = deduceFinalItemTypeOfFullDenture(
              data.productSpecificType,
              data.newFullDentureStep,
              data.isReplacement
            );
            if (savedItem) {
              await updateRemovableItem({
                variables: {
                  where: {
                    id: savedItem?.id,
                  },
                  data: {
                    teethShade: data.shade
                      ? {
                          connect: {
                            id: data.shade,
                          },
                        }
                      : {
                          disconnect: {
                            id: data.shade,
                          },
                        },
                    teeth: product.teeth,
                    product: {
                      connect: {
                        id: product.id,
                      },
                    },
                    isReplacement: {
                      set: data.isReplacement,
                    },
                    teethToManufacture: {
                      set: product.teeth,
                    },
                    itemType: {
                      set: finalItemType,
                    },
                    gingivaShade: data.gingivaShade
                      ? {
                          connect: {
                            id: data.gingivaShade,
                          },
                        }
                      : {
                          disconnect: {
                            id: data.gingivaShade,
                          },
                        },
                    itemMaterial:
                      finalItemType === ItemType.FULL_DENTURE
                        ? {
                            connect: {
                              id: "ACRYLIC",
                            },
                          }
                        : undefined,
                  },
                },
              });
            } else {
              await createRemovableItem({
                variables: {
                  args: {
                    teethShade: data.shade
                      ? {
                          connect: {
                            id: data.shade,
                          },
                        }
                      : undefined,
                    product: {
                      connect: {
                        id: product.id,
                      },
                    },
                    teeth: product.teeth,
                    teethToManufacture: {
                      set: product.teeth,
                    },
                    isReplacement: data.isReplacement,
                    itemType: finalItemType,
                    gingivaShade: data.gingivaShade
                      ? {
                          connect: {
                            id: data.gingivaShade,
                          },
                        }
                      : undefined,
                    itemMaterial:
                      finalItemType === ItemType.FULL_DENTURE
                        ? {
                            connect: {
                              id: "ACRYLIC",
                            },
                          }
                        : undefined,
                  },
                },
              });
            }
            await refetch();
            navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`);
          }}
        />
      );

    case ItemType.BRIDGE_IMPLANT:
      if (order && product_id) {
        return (
          <CreateNewImplantBridge
            accountProductPreferences={
              accountProductPreferences?.getAccountProductPreferences
            }
            anatomyItemChoicesData={anatomyItemChoicesData ?? []}
            order={order?.getOrderByUniqueAttribute}
            materialFormChoices={materialFormChoices}
            implantItemChoicesData={implantItemChoicesData}
            deleteManyImplantItem={async data => {
              await deleteManyImplantItems(data);
              await refetch();
            }}
            updateImplantItem={async data => {
              await updateImplantItem(data);
              await refetch();
            }}
            createImplantItem={async data => {
              const result = await createImplantItem(data);
              await refetch();
              return result;
            }}
            product_id={product_id}
            updateProduct={async data => {
              await updateProduct(data);
              await refetch();
            }}
            getUserProductPreferences={async () => {
              const preferences = await queryUserProductPreferences({
                variables: {
                  where: {
                    dentistId: {
                      equals: order?.user?.id,
                    },
                  },
                },
              });
              return preferences?.data?.getUserProductPreferences;
            }}
            onValidate={() =>
              navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`)
            }
          />
        );
      }
      break;
    case ItemType.IMPLANT_CROWN:
    case ItemType.IMPLANT:
      if (order && product_id && order?.user?.id !== null) {
        return (
          <CreateNewImplantCrown
            accountProductPreferences={
              accountProductPreferences?.getAccountProductPreferences
            }
            anatomyItemChoicesData={anatomyItemChoicesData ?? []}
            order={order?.getOrderByUniqueAttribute}
            materialFormChoices={materialFormChoices}
            abutmentMaterials={[]}
            implantItemChoicesData={implantItemChoicesData}
            deleteManyImplantItem={async data => {
              await deleteManyImplantItems(data);
              await refetch();
            }}
            updateImplantItem={async data => {
              await updateImplantItem(data);
              await refetch();
            }}
            createImplantItem={async data => {
              const result = await createImplantItem(data);
              await refetch();
              return result;
            }}
            product_id={product_id}
            updateProduct={async data => {
              await updateProduct(data);
              await refetch();
            }}
            getUserProductPreferences={async () => {
              const preferences = await queryUserProductPreferences({
                variables: {
                  where: {
                    dentistId: {
                      equals: order?.user?.id,
                    },
                  },
                },
              });
              return preferences?.data?.getUserProductPreferences;
            }}
            onValidate={() =>
              navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`)
            }
          />
        );
      }
      break;
    case ItemType.SCAN_ONLY:
      return (
        <CreateNewScanOnly
          onSubmit={async values => {
            await deleteManyRemovableItem({
              variables: {
                where: {
                  productId: {
                    equals: product_id,
                  },
                },
              },
            });
            if (
              [
                ItemType.RETENTION_WIRE,
                ItemType.CUSTOM_TRAY,
                ItemType.WAX_BITE,
              ].includes(values.scanOnlyType)
            ) {
              await updateProduct({
                variables: {
                  where: {
                    id: product_id,
                  },
                  data: {
                    teeth:
                      values?.archType === ArchTypeEn.UPPER_ARCH
                        ? UpperArchTeeth
                        : values?.archType === ArchTypeEn.LOWER_ARCH
                          ? LowerArchTeeth
                          : [...UpperArchTeeth, ...LowerArchTeeth],
                  },
                },
              });
              if (
                values?.archType === ArchTypeEn.UPPER_ARCH ||
                values?.archType === ArchTypeEn.BOTH_ARCH
              ) {
                await createRemovableItem({
                  variables: {
                    args: {
                      itemType: values.scanOnlyType,
                      teeth: UpperArchTeeth,
                      teethToManufacture: {
                        set: UpperArchTeeth,
                      },
                      product: {
                        connect: {
                          id: product_id,
                        },
                      },
                    },
                  },
                });
              }
              if (
                values?.archType === ArchTypeEn.LOWER_ARCH ||
                values?.archType === ArchTypeEn.BOTH_ARCH
              ) {
                await createRemovableItem({
                  variables: {
                    args: {
                      itemType: values.scanOnlyType,
                      teeth: LowerArchTeeth,
                      teethToManufacture: {
                        set: LowerArchTeeth,
                      },
                      product: {
                        connect: {
                          id: product_id,
                        },
                      },
                    },
                  },
                });
              }
            } else {
              await createRemovableItem({
                variables: {
                  args: {
                    itemType: values.scanOnlyType,
                    waxUpType: values.waxUpType,
                    product: {
                      connect: {
                        id: product_id,
                      },
                    },
                  },
                },
              });
            }
            await refetch();
            navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`);
          }}
        />
      );
    case ItemType.PARTIAL_DENTURE:
      return (
        <PartialDentureWorkflow
          materialChoices={materialFormChoices ?? []}
          gingivaShadeChoices={(
            anatomyItemChoicesData?.getItemShadesWhere ?? []
          )
            .filter(shade => shade.shadeType === ItemShadeType.GINGIVA)
            .map(shade => ({ value: shade.id, label: shade.label }))}
          order={order?.getOrderByUniqueAttribute}
          accountProductPreferences={
            accountProductPreferences?.getAccountProductPreferences
          }
          anatomyItemChoicesData={anatomyItemChoicesData ?? []}
          updateManyRemovableItem={async data => {
            await updateManyRemovableItem({
              variables: data,
            });
          }}
          updateOrder={async (data, teeth: number[]) => {
            const upperTeeth = getTeethsFromUpperArch(teeth ?? []);
            const lowerTeeth = getTeethsFromLowerArch(teeth ?? []);

            await deleteManyRemovableItem({
              variables: {
                where: {
                  productId: {
                    equals: product_id,
                  },
                },
              },
            });
            await updateProduct({
              variables: {
                where: {
                  id: product_id,
                },
                data: {
                  teeth: teeth,
                },
              },
            });
            if (upperTeeth.length > 0) {
              data.teeth = upperTeeth;
              data.teethToManufacture = {
                set: upperTeeth,
              };
              await createRemovableItem({
                variables: {
                  args: data,
                },
              });
            }
            if (lowerTeeth.length > 0) {
              data.teeth = lowerTeeth;
              data.teethToManufacture = {
                set: lowerTeeth,
              };
              await createRemovableItem({
                variables: {
                  args: data,
                },
              });
            }
          }}
          product_id={product_id}
          onSubmit={async () => {
            await refetch();
            navigate(`/orders/${order?.getOrderByUniqueAttribute?.id}/edit/`);
          }}
        />
      );
    default:
      return "undefined";
  }
};

export default NewItemComponent;
